import React, { useState, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Tag,
  message,
  Tooltip,
  theme,
  Drawer,
  Empty,
  Badge,
  Collapse,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  DownloadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
  StarOutlined,
  CheckCircleFilled,
  ReloadOutlined,
  FolderOpenOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { SourceEpisode, SourcePanel, SourceProcess, SourceScene } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { sourceStoriesApi } from '../../services/api/sourceStories';
import { fileService } from '../../services/fileService';
import { useApi, useAsyncOperation, useIsMobile } from '../../hooks';
import { formatDate } from '../../utils';
import EditEpisodeModal from './EditEpisodeModal';
import EpisodeContentModal from './EpisodeContentModal';
import VideoEditorModal from './VideoEditorModal';
import HookEditorInline from './HookEditorInline';
import HookPanelImagePickerModal from './HookPanelImagePickerModal';

const { Title, Text } = Typography;

function panelMetaFromEpisode(episode: SourceEpisode, panelId: string | undefined) {
  if (!panelId || !episode.panels?.length) return null;
  return episode.panels.find((p) => p.panelId === panelId) ?? null;
}

function buildPanelRangeLines(
  panelId: string | undefined,
  episode: SourceEpisode,
  panelMap: Map<string, SourcePanel>,
  sceneMap: Map<string, SourceScene>,
  processMap: Map<string, SourceProcess>
): { summary: string; detail: string } {
  if (!panelId) {
    return { summary: '—', detail: '' };
  }

  const panel = panelMap.get(panelId);
  const meta = panelMetaFromEpisode(episode, panelId);

  if (!panel) {
    const ord = meta?.orderIndex;
    const shortId = `${panelId.slice(0, 8)}…`;
    const summary =
      ord != null ? `Episode panel #${ord} (loading…)` : `Panel ${shortId}`;
    return { summary, detail: '' };
  }

  const scene = sceneMap.get(panel.sceneId);
  const processId = scene?.processId ?? panel.processId;
  const proc = processId ? processMap.get(processId) : undefined;
  const processName = proc?.name ?? 'Process';
  const sceneTitle = scene?.title ?? 'Scene';
  const sceneNum = scene?.orderIndex ?? '?';

  // Table: Process > Scene #n: title > Panel #m
  const summary = `${processName} > Scene #${sceneNum}: ${sceneTitle} > Panel #${panel.sceneOrderIndex}`;

  const detail = [
    `Process:${processName}`,
    `Scene:#${sceneNum} ${sceneTitle}`,
    `Panel:#${panel.sceneOrderIndex}`,
  ].join('\n');

  return { summary, detail };
}

/** Ant Design Tooltip ignores \n in string titles; pre-line makes each block break correctly */
function RangeTooltipTitle({ text }: { text: string }) {
  return (
    <div style={{ whiteSpace: 'pre-line', maxWidth: 320, textAlign: 'left' }}>
      {text}
    </div>
  );
}

interface EpisodesTabProps {
  storyId: string;
}

const EpisodesTab: React.FC<EpisodesTabProps> = ({ storyId }) => {
  const isMobile = useIsMobile();
  const { token: { colorBorder } } = theme.useToken();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<SourceEpisode | null>(null);
  const [contentEpisode, setContentEpisode] = useState<SourceEpisode | null>(null);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [videoEditorEpisode, setVideoEditorEpisode] = useState<SourceEpisode | null>(null);
  const [isVideoEditorVisible, setIsVideoEditorVisible] = useState(false);
  const [isHookDrawerVisible, setIsHookDrawerVisible] = useState(false);
  const [hookEpisodeId, setHookEpisodeId] = useState<string | null>(null);
  const [generatingHookId, setGeneratingHookId] = useState<string | null>(null);
  const [selectingImagesId, setSelectingImagesId] = useState<string | null>(null);
  const [applyingHookId, setApplyingHookId] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [isCreatingHook, setIsCreatingHook] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState<{
    hookId: string;
    panelId: string;
  } | null>(null);
  const [editingHookId, setEditingHookId] = useState<string | null>(null);

  const { execute: executeAsync } = useAsyncOperation();

  const { data: chaptersData } = useApi(
    useCallback(() => sourceStoriesApi.getSourceChapters(storyId, { pageSize: 1000 }), [storyId]),
    { immediate: !!storyId }
  );
  const { data: processesData } = useApi(
    useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]),
    { immediate: !!storyId }
  );
  const { data: scenesData } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourceScenes(storyId), [storyId]),
    { immediate: !!storyId }
  );
  const { data: panelsData } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]),
    { immediate: !!storyId }
  );

  const processMap = useMemo(() => {
    const list = processesData?.data ?? [];
    return new Map<string, SourceProcess>(list.map((p) => [p.id, p]));
  }, [processesData]);

  const sceneMap = useMemo(() => {
    const list = scenesData?.data ?? [];
    return new Map<string, SourceScene>(list.map((s) => [s.id, s]));
  }, [scenesData]);

  const panelMap = useMemo(() => {
    const list = panelsData?.data ?? [];
    return new Map<string, SourcePanel>(list.map((p) => [p.id, p]));
  }, [panelsData]);

  const {
    data: episodesData,
    loading: episodesLoading,
    refresh: refreshEpisodes,
  } = useApi(
    useCallback(() => sourceEpisodesApi.getEpisodes(storyId), [storyId]),
    { immediate: true }
  );

  const chapters = useMemo(() => chaptersData?.data || [], [chaptersData]);

  const treeData = useMemo(() => {
    const episodes = episodesData?.data || [];
    if (episodes.length === 0) return [];

    const groups = new Map<string, SourceEpisode[]>();
    const unknownGroup: SourceEpisode[] = [];

    episodes.forEach((episode) => {
      const chapterId = episode.metadata?.startChapterId;
      if (chapterId) {
        if (!groups.has(chapterId)) {
          groups.set(chapterId, []);
        }
        groups.get(chapterId)!.push(episode);
      } else {
        unknownGroup.push(episode);
      }
    });

    const rootNodes: any[] = [];

    // Create a copy of chapters and sort them descending by orderIndex
    const sortedChapters = [...chapters].sort((a, b) => b.orderIndex - a.orderIndex);

    // Add groups from chapters in descending order
    sortedChapters.forEach((chapter) => {
      const chapterEpisodes = groups.get(chapter.id);
      if (chapterEpisodes && chapterEpisodes.length > 0) {
        // Episodes within a chapter should still be ascending
        chapterEpisodes.sort((a, b) => a.orderIndex - b.orderIndex);
        rootNodes.push({
          id: `chapter-${chapter.id}`,
          isGroup: true,
          name: chapter.name || `Chapter ${chapter.orderIndex}`,
          children: chapterEpisodes,
        });
      }
    });

    // Add unknown group if exists (at the bottom)
    if (unknownGroup.length > 0) {
      unknownGroup.sort((a, b) => a.orderIndex - b.orderIndex);
      rootNodes.push({
        id: 'chapter-unknown',
        isGroup: true,
        name: 'Unknown Chapter',
        children: unknownGroup,
      });
    }

    return rootNodes;
  }, [episodesData, chapters]);

  const hookEpisode = useMemo(() => {
    return (episodesData?.data || []).find(e => e.id === hookEpisodeId) || null;
  }, [episodesData, hookEpisodeId]);

  const handleRefreshAll = useCallback(() => {
    refreshEpisodes();
  }, [refreshEpisodes]);

  const getVideoFormForEpisode = useCallback((episode: SourceEpisode | null) => {
    if (!episode || !episode.startPanelId) return 'LONG';
    const panel = panelMap.get(episode.startPanelId);
    if (!panel) return 'LONG';
    const scene = sceneMap.get(panel.sceneId);
    const processId = scene?.processId ?? panel.processId;
    if (!processId) return 'LONG';
    const process = processMap.get(processId);
    return process?.videoForm || 'LONG';
  }, [panelMap, sceneMap, processMap]);

  const handleCreate = () => {
    setEditingEpisode(null);
    setIsModalVisible(true);
  };

  const handleEdit = (episode: SourceEpisode) => {
    setEditingEpisode(episode);
    setIsModalVisible(true);
  };

  const handleDelete = async (episode: SourceEpisode) => {
    await executeAsync(
      () => sourceEpisodesApi.deleteEpisode(episode.id),
      { successMessage: 'Episode deleted successfully!' }
    );
    refreshEpisodes();
  };
  
  const handleDownload = async (episode: SourceEpisode) => {
    try {
      message.loading({ content: 'Preparing download...', key: 'download' });
      const blob = await sourceEpisodesApi.exportEpisodeMaterials(episode.id);
      fileService.saveFile(blob, `episode-${episode.id}-export.zip`);
      message.success({ content: 'Download complete!', key: 'download', duration: 2 });
    } catch (error) {
      message.error({ content: 'Failed to download materials', key: 'download', duration: 2 });
      console.error('Download error:', error);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Tag color="default">Draft</Tag>;
      case 'PUBLISHED':
        return <Tag color="success">Published</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getAudioStatusTag = (status: string | undefined) => {
    switch (status) {
      case 'generating':
        return <Tag icon={<SyncOutlined spin />} color="processing">Generating</Tag>;
      case 'ready':
        return <Tag icon={<CheckCircleOutlined />} color="success">Ready</Tag>;
      case 'error':
        return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
      default:
        return <Tag color="default">No Audio</Tag>;
    }
  };

  const handleGenerateHook = async (episodeId: string) => {
    setGeneratingHookId(episodeId);
    const msgKey = `gen-hook-${episodeId}`;
    message.loading({ content: 'Đang sáng tạo Hook Intro cho episode...', key: msgKey, duration: 0 });

    try {
      await sourceEpisodesApi.generateHookConcept(episodeId);
      message.success({ content: 'Đã tạo xong Hook Intro!', key: msgKey, duration: 3 });
      refreshEpisodes();
    } catch (error: any) {
      console.error('Hook generation error:', error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.messages?.[0] || errorData?.message || error.message || 'Không rõ nguyên nhân';
      message.error({ content: `Lỗi: ${errorMessage}`, key: msgKey, duration: 5 });
    } finally {
      setGeneratingHookId(null);
    }
  };

  const handleSelectHookImages = async (episodeId: string, hookId: string) => {
    setSelectingImagesId(hookId);
    const msgKey = `select-images-${hookId}`;
    message.loading({ content: 'AI đang chọn ảnh phù hợp cho các hook panels...', key: msgKey, duration: 0 });

    try {
      await sourceEpisodesApi.selectHookPanelImages(episodeId, hookId);
      message.success({ content: 'Đã chọn ảnh xong!', key: msgKey, duration: 3 });
      refreshEpisodes();
    } catch (error: any) {
      console.error('Select hook images error:', error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.messages?.[0] || errorData?.message || error.message || 'Không rõ nguyên nhân';
      message.error({ content: `Lỗi: ${errorMessage}`, key: msgKey, duration: 5 });
    } finally {
      setSelectingImagesId(null);
    }
  };

  const handleApplyHook = async (episodeId: string, hookId: string) => {
    setApplyingHookId(hookId);
    const msgKey = `apply-hook-${hookId}`;
    message.loading({ content: 'Đang apply hook vào episode...', key: msgKey, duration: 0 });

    try {
      await sourceEpisodesApi.applyHook(episodeId, hookId);
      message.success({ content: 'Hook đã được apply! Hãy gen lại audio và render video.', key: msgKey, duration: 5 });
      refreshEpisodes();
    } catch (error: any) {
      console.error('Apply hook error:', error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.messages?.[0] || errorData?.message || error.message || 'Không rõ nguyên nhân';
      message.error({ content: `Lỗi: ${errorMessage}`, key: msgKey, duration: 5 });
    } finally {
      setApplyingHookId(null);
    }
  };

  const handleHookCreated = async (updatedEpisode: SourceEpisode) => {
    setIsCreatingHook(false);
    refreshEpisodes();
  };

  const handleHookUpdated = async (updatedEpisode: SourceEpisode) => {
    setEditingHookId(null);
    refreshEpisodes();
  };

  const handleImagePickerSuccess = (updatedEpisode: SourceEpisode) => {
    setImagePickerTarget(null);
    refreshEpisodes();
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => {
        if (record.isGroup) {
          return (
            <Space>
              <FolderOpenOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              <Text strong style={{ fontSize: '15px' }}>{name}</Text>
            </Space>
          );
        }

        const videoForm = getVideoFormForEpisode(record);
        const cssAspect = videoForm === 'SHORT' ? '9/16' : '16/9';
        const panelCount = record.panels?.length || 0;

        return (
          <Space direction="vertical" size={0}>
            <Space>
              <Text strong>{name}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>({panelCount} panels)</Text>
            </Space>
            {record.thumbnailUrl && (
              <div style={{ marginTop: 4, width: 80, aspectRatio: cssAspect, overflow: 'hidden', borderRadius: 4 }}>
                <img src={record.thumbnailUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Order',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 80,
      render: (index: number, record: any) => record.isGroup ? null : <Tag color="blue">{index}</Tag>,
    },
    {
      title: 'Type',
      key: 'videoForm',
      width: 100,
      render: (_: any, record: any) => {
        if (record.isGroup) return null;
        const videoForm = record.metadata?.videoForm;
        if (videoForm === 'LONG') {
          return <Tag color="blue">LONG</Tag>;
        }
        if (videoForm === 'SHORT') {
          return <Tag color="purple">SHORT</Tag>;
        }
        return <Tag color="default">N/A</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => {
        if (record.isGroup) return null;
        return (
          <Space direction="vertical" size={4}>
            {getStatusTag(status)}
            {getAudioStatusTag(record.audioStatus)}
          </Space>
        );
      },
    },
    {
      title: 'Range',
      key: 'range',
      width: 380,
      render: (_: unknown, record: any) => {
        if (record.isGroup) return null;
        const start = buildPanelRangeLines(record.startPanelId, record, panelMap, sceneMap, processMap);
        const end = buildPanelRangeLines(record.endPanelId, record, panelMap, sceneMap, processMap);
        return (
          <Space direction="vertical" size={4}>
            <Tooltip title={start.detail ? <RangeTooltipTitle text={start.detail} /> : undefined}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>Start: </Text>
                {start.summary}
              </Text>
            </Tooltip>
            <Tooltip title={end.detail ? <RangeTooltipTitle text={end.detail} /> : undefined}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>End: </Text>
                {end.summary}
              </Text>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Last Modified',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      render: (date: string, record: any) => record.isGroup ? null : formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: any) => {
        if (record.isGroup) return null;
        return (
          <Space>
            <Tooltip title="View Content">
              <Button
                type="text"
                icon={<FileTextOutlined />}
                onClick={() => {
                  setContentEpisode(record);
                  setIsContentVisible(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Video Editor">
              <Button
                type="text"
                icon={<VideoCameraOutlined />}
                onClick={async () => {
                  // Fetch fresh episode data from API
                  const freshEpisode = await sourceEpisodesApi.getEpisodeDetail(record.id);
                  setVideoEditorEpisode(freshEpisode.data);
                  setIsVideoEditorVisible(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Manage Hooks">
              <Button
                type="text"
                icon={<ThunderboltOutlined />}
                onClick={() => {
                  setHookEpisodeId(record.id);
                  setIsHookDrawerVisible(true);
                }}
                style={{ color: '#faad14' }}
              />
            </Tooltip>
            <Tooltip title="Download Materials">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
            <Popconfirm
              title="Are you sure you want to delete this episode?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const EpisodeCard = ({ episode }: { episode: SourceEpisode }) => {
    const videoForm = getVideoFormForEpisode(episode);
    const cssAspect = videoForm === 'SHORT' ? '9/16' : '16/9';
    const start = buildPanelRangeLines(episode.startPanelId, episode, panelMap, sceneMap, processMap);
    const end = buildPanelRangeLines(episode.endPanelId, episode, panelMap, sceneMap, processMap);
    const panelCount = episode.panels?.length || 0;

    return (
      <Card
        size="small"
        style={{ marginBottom: 12, borderRadius: 8 }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {episode.thumbnailUrl && (
            <div style={{ width: 80, aspectRatio: cssAspect, overflow: 'hidden', borderRadius: 4, flexShrink: 0 }}>
              <img src={episode.thumbnailUrl} alt={episode.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Text strong style={{ fontSize: 15, display: 'block' }}>{episode.name}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>{panelCount} panels</Text>
              </div>
              <Tag color="blue">#{episode.orderIndex}</Tag>
            </div>
            <Space size={4} style={{ marginTop: 4 }} wrap>
              {getStatusTag(episode.status)}
              {getAudioStatusTag(episode.audioStatus)}
              {episode.metadata?.videoForm === 'LONG' && <Tag color="blue">LONG</Tag>}
              {episode.metadata?.videoForm === 'SHORT' && <Tag color="purple">SHORT</Tag>}
              {!episode.metadata?.videoForm && <Tag color="default">N/A</Tag>}
            </Space>
          </div>
        </div>

        <div style={{ marginBottom: 12, fontSize: 12 }}>
          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" strong>Start: </Text>
            <Text type="secondary">{start.summary}</Text>
          </div>
          <div>
            <Text type="secondary" strong>End: </Text>
            <Text type="secondary">{end.summary}</Text>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          paddingTop: 8,
          borderTop: `1px solid ${colorBorder}44`
        }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {formatDate(episode.modifiedAt)}
          </Text>
          <Space size={4}>
            <Button
              type="text"
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={() => {
                setHookEpisodeId(episode.id);
                setIsHookDrawerVisible(true);
              }}
              style={{ backgroundColor: '#fffbe6', color: '#faad14' }}
            />
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => {
                setContentEpisode(episode);
                setIsContentVisible(true);
              }}
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <Button
              type="text"
              size="small"
              icon={<VideoCameraOutlined />}
              onClick={async () => {
                const freshEpisode = await sourceEpisodesApi.getEpisodeDetail(episode.id);
                setVideoEditorEpisode(freshEpisode.data);
                setIsVideoEditorVisible(true);
              }}
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(episode)}
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <Popconfirm
              title="Delete this episode?"
              onConfirm={() => handleDelete(episode)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ backgroundColor: '#fff1f0' }}
              />
            </Popconfirm>
          </Space>
        </div>
      </Card>
    );
  };

  return (
    <Card styles={{ body: isMobile ? { padding: '12px 8px' } : undefined }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 16 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            Episode Management
          </Title>
          <Button 
            type="default" 
            shape="circle"
            icon={<ReloadOutlined spin={episodesLoading} />} 
            onClick={handleRefreshAll}
            title="Refresh Data"
            style={{ marginLeft: 8 }}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? 'Create' : 'Create Episode'}
        </Button>
      </div>

      {!isMobile ? (
        <Table
          columns={columns as any}
          dataSource={treeData}
          rowKey="id"
          loading={episodesLoading}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) => {
              if (expanded) {
                setExpandedRowKeys([...expandedRowKeys, record.id]);
              } else {
                setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
              }
            },
          }}
        />
      ) : (
        <div style={{ marginTop: 8 }}>
          {episodesLoading && treeData.length === 0 ? (
            <Card loading />
          ) : (
            <Collapse 
              ghost 
              expandIconPosition="end"
            >
              {treeData.map(group => (
                <Collapse.Panel 
                  header={
                    <Space>
                      <FolderOpenOutlined style={{ color: '#1890ff' }} />
                      <Text strong>{group.name}</Text>
                      <Badge count={group.children.length} style={{ backgroundColor: '#1890ff' }} />
                    </Space>
                  } 
                  key={group.id}
                >
                  {group.children.map((episode: SourceEpisode) => (
                    <EpisodeCard key={episode.id} episode={episode} />
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </div>
      )}

      <EditEpisodeModal
        visible={isModalVisible}
        episode={editingEpisode}
        storyId={storyId}
        onClose={() => {
          setIsModalVisible(false);
          setEditingEpisode(null);
        }}
        onSuccess={() => {
          setIsModalVisible(false);
          setEditingEpisode(null);
          refreshEpisodes();
        }}
        nextOrderIndex={(episodesData?.data?.length || 0) + 1}
      />

      {videoEditorEpisode && (
        <VideoEditorModal
          visible={isVideoEditorVisible}
          episode={videoEditorEpisode}
          storyId={storyId}
          videoForm={getVideoFormForEpisode(videoEditorEpisode)}
          onClose={() => {
            setIsVideoEditorVisible(false);
            setVideoEditorEpisode(null);
          }}
          onSuccess={async (updatedEpisode?: SourceEpisode) => {
            await refreshEpisodes();
            if (updatedEpisode) {
              setVideoEditorEpisode(updatedEpisode);
            } else {
              // Refresh episode data from API
              const refreshed = await sourceEpisodesApi.getEpisodeDetail(videoEditorEpisode.id);
              setVideoEditorEpisode(refreshed.data);
            }
          }}
        />
      )}

      {contentEpisode && (
        <EpisodeContentModal
          visible={isContentVisible}
          episode={contentEpisode}
          onClose={() => {
            setIsContentVisible(false);
            setContentEpisode(null);
          }}
        />
      )}

      <Drawer
        title={
          <div style={{ width: '100%', paddingRight: 32 }}>
            <div style={{ marginBottom: 4, lineHeight: '1.2' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Episode Hooks
              </Text>
              <Tooltip title={hookEpisode?.name}>
                <Text strong style={{ fontSize: 15, display: 'block' }} ellipsis>
                  {hookEpisode?.name}
                </Text>
              </Tooltip>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Space size={8}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setIsCreatingHook(true);
                    setEditingHookId(null);
                  }}
                  disabled={isCreatingHook || !!editingHookId}
                >
                  Manual
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => hookEpisode && handleGenerateHook(hookEpisode.id)}
                  loading={!!generatingHookId}
                  disabled={!hookEpisode?.startPanelId || !hookEpisode?.endPanelId}
                >
                  AI Generate
                </Button>
              </Space>
            </div>
          </div>
        }
        placement="right"
        width={isMobile ? '100%' : 600}
        onClose={() => {
          setIsHookDrawerVisible(false);
          setHookEpisodeId(null);
          setIsCreatingHook(false);
        }}
        open={isHookDrawerVisible}
      >
        {!hookEpisode ? (
          <Empty description="No episode selected" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {isCreatingHook && (
              <HookEditorInline
                episodeId={hookEpisode.id}
                onSuccess={handleHookCreated}
                onCancel={() => setIsCreatingHook(false)}
              />
            )}
            {imagePickerTarget && (
              <HookPanelImagePickerModal
                open={imagePickerTarget !== null}
                episodeId={hookEpisode.id}
                hookId={imagePickerTarget.hookId}
                panelId={imagePickerTarget.panelId}
                episodePanels={Array.from(panelMap.values())}
                onSuccess={handleImagePickerSuccess}
                onClose={() => setImagePickerTarget(null)}
              />
            )}
            {(() => {
              const hooks = [...((hookEpisode.hooks as any[]) || [])].reverse();
              const appliedHookId = (hookEpisode.panels as any[])?.find((p: any) => p.type === 'hook')?.hookId;

              if (hooks.length === 0) {
                return (
                  <Empty 
                    description="No hooks generated yet" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button 
                      type="primary" 
                      onClick={() => handleGenerateHook(hookEpisode.id)}
                      loading={generatingHookId === hookEpisode.id}
                    >
                      Create First Hook
                    </Button>
                  </Empty>
                );
              }

              return hooks.map((hook: any, index: number) => {
                // Since we reversed, original index is hooks.length - 1 - index
                const originalIndex = hooks.length - 1 - index;

                if (editingHookId === hook.id) {
                  return (
                    <HookEditorInline
                      key={hook.id}
                      episodeId={hookEpisode.id}
                      existingHook={hook}
                      onSuccess={handleHookUpdated}
                      onCancel={() => setEditingHookId(null)}
                    />
                  );
                }

                return (
                  <Card 
                    key={hook.id} 
                    size="small" 
                    title={`Hook Version ${originalIndex + 1}`} 
                    style={appliedHookId === hook.id ? { border: '1px solid #52c41a', boxShadow: '0 0 8px rgba(82, 196, 26, 0.1)' } : {}}
                    extra={
                      <Space>
                        {appliedHookId === hook.id ? (
                          <Tag color="success" icon={<CheckCircleFilled />}>Applied</Tag>
                        ) : (
                          <Space>
                              <Button 
                                size="small" 
                                type="primary"
                                style={{ backgroundColor: '#52c41a', border: 'none' }}
                                onClick={() => handleApplyHook(hookEpisode.id, hook.id)}
                                loading={applyingHookId === hook.id}
                              >
                                Apply
                              </Button>
                              <Tooltip title="Edit Hook">
                                <Button
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => {
                                    setEditingHookId(hook.id);
                                    setIsCreatingHook(false);
                                  }}
                                />
                              </Tooltip>
                          </Space>
                        )}
                        <Button 
                          size="small" 
                          icon={<VideoCameraOutlined />} 
                          onClick={() => handleSelectHookImages(hookEpisode.id, hook.id)}
                          loading={selectingImagesId === hook.id}
                          disabled={appliedHookId === hook.id}
                        >
                          Chọn ảnh
                        </Button>
                        <Tag color="gold" icon={<StarOutlined />}>{hook.reviewScore} pts</Tag>
                      </Space>
                    }
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Concept: </Text>
                      <Text>{hook.concept}</Text>
                    </div>
                    <div style={{ paddingLeft: 16, borderLeft: '2px solid #f0f0f0' }}>
                      {hook.panels.map((panel: any, pIdx: number) => (
                        <div key={panel.id} style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start' }}>
                          <Text type="secondary" style={{ marginRight: 8, marginTop: 4 }}>{pIdx + 1}. </Text>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                              <Text style={{ flex: 1 }}>{panel.caption}</Text>
                              <Button
                                size="small"
                                type="text"
                                icon={<PictureOutlined />}
                                onClick={() => setImagePickerTarget({ hookId: hook.id, panelId: panel.id })}
                                style={{ color: '#1890ff' }}
                              >
                                {panel.imageUrl ? 'Đổi ảnh' : 'Chọn ảnh'}
                              </Button>
                            </div>
                            
                            <div 
                              style={{ 
                                width: 100, 
                                aspectRatio: '1/1', 
                                overflow: 'hidden', 
                                borderRadius: 4, 
                                border: '1px solid #f0f0f0',
                                backgroundColor: '#f5f5f5',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onClick={() => setImagePickerTarget({ hookId: hook.id, panelId: panel.id })}
                            >
                              {panel.imageUrl ? (
                                <img src={panel.imageUrl} alt={`Hook ${pIdx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              ) : (
                                <PictureOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {hook.reviewData && (
                      <div style={{ marginTop: 12 }}>
                        <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>Audience Scores: </Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {hook.reviewData.map((rd: any, rdIdx: number) => (
                            <Tooltip key={rdIdx} title={rd.feedbacks.length > 0 ? rd.feedbacks.join(' | ') : 'No feedback'}>
                              <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>{rd.audience}: {rd.score}</Tag>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              });
            })()}
          </div>
        )}
      </Drawer>
    </Card>
  );
};

export default EpisodesTab;
